import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ClipboardList, Clock } from 'lucide-react';
import { Text } from '@/Components/ui/typography';
import { Button } from '@/Components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/dialog';
import {
  formatFeedbackAnswerForDisplay,
  inferFeedbackQuestionType,
  parseSkillMatrixAnswer,
} from '@/LiveInterview/utils/interviewerFeedbackUtils';

const formatAnswerLabel = (item) =>
  item?.label || item?.question || item?.prompt || item?.id || 'Response';

const formatTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

function FeedbackAnswerValue({ item, questions = [] }) {
  const questionType = inferFeedbackQuestionType(item, questions);
  const displayValue = formatFeedbackAnswerForDisplay(item, questions);

  if (!displayValue) {
    return null;
  }

  if (questionType === 'skill_matrix') {
    const ratings = parseSkillMatrixAnswer(item?.answer);
    const entries = Object.entries(ratings).filter(([, rating]) => String(rating || '').trim());
    if (!entries.length) {
      return null;
    }
    return (
      <dl className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
        {entries.map(([skill, rating]) => (
          <div key={skill} className="flex items-start justify-between gap-3 text-sm">
            <dt className="font-medium text-slate-700">{skill}</dt>
            <dd className="text-right font-semibold text-slate-900">{rating}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dd className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-800">{displayValue}</dd>
  );
}

function FeedbackAnswersList({ answers, questions = [], emptyMessage }) {
  const filled = (answers || []).filter(
    (item) => item && String(formatFeedbackAnswerForDisplay(item, questions) || '').trim()
  );
  if (!filled.length) {
    return emptyMessage ? (
      <Text size="sm" className="text-slate-600">
        {emptyMessage}
      </Text>
    ) : null;
  }
  return (
    <dl className="space-y-4">
      {filled.map((item) => (
        <div key={item.id || formatAnswerLabel(item)}>
          <dt className="text-sm font-semibold text-slate-800">{formatAnswerLabel(item)}</dt>
          <FeedbackAnswerValue item={item} questions={questions} />
        </div>
      ))}
    </dl>
  );
}

function FeedbackStatusCopy({ status, record }) {
  if (status === 'submitted') {
    const submittedLabel = formatTimestamp(record?.submitted_at);
    return (
      <Text size="sm" className="text-slate-600">
        {submittedLabel
          ? `Submitted on ${submittedLabel}.`
          : 'Interviewer feedback has been submitted.'}
      </Text>
    );
  }
  if (status === 'deferred') {
    const sentLabel = formatTimestamp(record?.deferred_email_sent_at);
    return (
      <Text size="sm" className="text-slate-600">
        The interviewer chose to complete feedback later.
        {sentLabel ? ` A secure link was emailed on ${sentLabel}.` : ' A secure email link was sent.'}
      </Text>
    );
  }
  if (status === 'pending') {
    return (
      <Text size="sm" className="text-slate-600">
        Interviewer feedback is in progress and has not been submitted yet.
      </Text>
    );
  }
  return null;
}

export default function InterviewerFeedbackSummary({
  feedback,
  compact = false,
  insightsMode = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const record = feedback && typeof feedback === 'object' ? feedback : null;
  const status = String(record?.status || '').trim().toLowerCase();
  const questionDefs = record?.questions || [];

  const answers = useMemo(
    () =>
      (record?.answers || []).filter(
        (item) => item && String(formatFeedbackAnswerForDisplay(item, questionDefs) || '').trim()
      ),
    [record?.answers, questionDefs]
  );

  if (!record) return null;

  if (!insightsMode && status !== 'submitted') {
    return null;
  }

  if (insightsMode && !['submitted', 'deferred', 'pending'].includes(status)) {
    return null;
  }

  const submittedLabel = formatTimestamp(record.submitted_at);
  const isSubmitted = status === 'submitted';
  const isDeferred = status === 'deferred';

  const previewAnswers = isSubmitted ? answers.slice(0, 3) : answers.slice(0, 1);
  const statusTitle = isSubmitted
    ? 'Interviewer feedback'
    : isDeferred
      ? 'Feedback pending'
      : 'Feedback in progress';
  const statusTone = isSubmitted
    ? 'border-emerald-200 bg-emerald-50/60'
    : 'border-amber-200 bg-amber-50/70';

  if (insightsMode) {
    return (
      <>
        <div className={`rounded-xl border p-4 ${statusTone}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  isSubmitted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}
              >
                {isSubmitted ? (
                  <ClipboardList className="h-4 w-4" aria-hidden />
                ) : (
                  <Clock className="h-4 w-4" aria-hidden />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <Text size="sm" className="font-semibold text-slate-900">
                  {statusTitle}
                </Text>
                <div className="mt-1">
                  <FeedbackStatusCopy status={status} record={record} />
                </div>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
              Feedback
            </Button>
          </div>

          {previewAnswers.length ? (
            <dl className="mt-4 space-y-3 border-t border-white/70 pt-4">
              {previewAnswers.map((item) => (
                <div key={item.id || formatAnswerLabel(item)}>
                  <dt className="text-sm font-semibold text-slate-800">{formatAnswerLabel(item)}</dt>
                  <div className="mt-1 line-clamp-3 text-sm leading-6 text-slate-800">
                    {inferFeedbackQuestionType(item, questionDefs) === 'skill_matrix' ? (
                      <FeedbackAnswerValue item={item} questions={questionDefs} />
                    ) : (
                      String(formatFeedbackAnswerForDisplay(item, questionDefs) || '').trim()
                    )}
                  </div>
                </div>
              ))}
            </dl>
          ) : null}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{statusTitle}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <FeedbackStatusCopy status={status} record={record} />
                </div>
              </DialogDescription>
            </DialogHeader>
            <FeedbackAnswersList
              answers={record.answers}
              questions={questionDefs}
              emptyMessage={
                isDeferred
                  ? 'No draft answers yet. The interviewer can complete feedback from the emailed link.'
                  : 'No answers recorded yet.'
              }
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (!answers.length) {
    return null;
  }

  if (compact) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <button
          type="button"
          className="flex w-full items-start gap-3 text-left"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls="interviewer-feedback-answers"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <ClipboardList className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <Text size="sm" className="font-semibold text-slate-900">
                Interviewer feedback
              </Text>
              {expanded ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              )}
            </div>
            {submittedLabel ? (
              <Text size="xs" className="mt-1 text-slate-500">
                Submitted {submittedLabel}
              </Text>
            ) : null}
            {!expanded ? (
              <Text size="xs" className="mt-1 text-emerald-800">
                Click to view feedback details
              </Text>
            ) : null}
          </div>
        </button>

        {expanded ? (
          <div
            id="interviewer-feedback-answers"
            className="mt-3 border-t border-emerald-100/80 pt-3"
          >
            <FeedbackAnswersList answers={answers} questions={questionDefs} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
          <ClipboardList className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <Text size="sm" className="font-semibold text-slate-900">
            Interviewer feedback
          </Text>
          {submittedLabel ? (
            <Text size="xs" className="mt-1 text-slate-500">
              Submitted {submittedLabel}
            </Text>
          ) : null}
        </div>
      </div>

      <FeedbackAnswersList answers={answers} questions={questionDefs} />
    </div>
  );
}
